<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :embedded="embedded"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">
    <div class="h-100">
      <ReportFilterPopover
        headerTitle="Stock Transaction Filter"
        :showApplyButton="true"
        :showResetButton="true"
        :appendToBody="true"
        alignment="left"
        placement="bottom-end"
        @apply="applyStockTransactionFilter"
        @reset="resetStockTransactionFilter">
        <template v-slot:popover>
          <ReportFilterGroup label="Date Filter">
            <ReportDateRangeSelect
              :startDate="editingStockTransactionFilter.startDate"
              :endDate="editingStockTransactionFilter.endDate"
              @updateStartDate="editingStockTransactionFilter.startDate = $event"
              @updateEndDate="editingStockTransactionFilter.endDate = $event"
            />
          </ReportFilterGroup>
          <ReportFilterGroup label="Stock Filter" v-if="stockFilterEnabled">
            <FormInputStockCode
              v-model="editingStockTransactionFilter.stockId"
              @updateStockInfo="editingStockTransactionFilter.stockInfo = $event"
              placeholder="Stock filter"
              :dropdownAutoWidth="true"
              :appendToBody="true"
              :stockType="InventoryStockTypes.glass" />
          </ReportFilterGroup>
          <ReportFilterGroup label="Transaction Type Filter">
            <FormMultiSelect
              v-model="editingStockTransactionFilter.type"
              :options="StockTransactionTypeLookup"
              :dropdownAutoWidth="true"
              :appendToBody="true"
              placeholder="Select types"
            />
          </ReportFilterGroup>
          <ReportFilterGroup label="Transaction Direction Filter">
            <FormMultiSelect
              v-model="editingStockTransactionFilter.direction"
              :options="StockTransactionDirectionTypeLookup"
              :dropdownAutoWidth="true"
              :appendToBody="true"
              placeholder="Select directions"
            />
          </ReportFilterGroup>
        </template>
        <template v-slot:filterDisplay>
          <div>
            <i class="far fa-calendar mr-2"></i>{{appliedStockTransactionFilter.startDate | formatDate}} - {{appliedStockTransactionFilter.endDate | formatDate}}
          </div>
          <div v-if="hasStockFilter">
            <i class="far fa-inventory mr-2"></i>{{appliedStockTransactionFilter.stockInfo ? appliedStockTransactionFilter.stockInfo.name : 'None'}}
          </div>
        </template>
      </ReportFilterPopover>

      <ChartBar
        title="Transactions"
        :series="stockTransactionData.series"
        :xAxis="stockTransactionData.xAxis"
        :yAxis="stockTransactionData.yAxis"
        :legend="stockTransactionData.legend"
        :options="stockTransactionData.options"
        :popupEnabled="true"
        :exportEnabled="true"
      />
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'
import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'

import ChartBar from '@/components/charts/chartBar'
import { InventoryStockTypes, StockTransactionTypes, StockTransactionTypeLookup, StockTransactionDirectionTypes, StockTransactionDirectionTypeLookup } from '@/store/types'

export default {
  name: 'InventoryStockTransactionChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportFilterGroup,
    FormInputStockCode
  },
  props: {
    title: String,
    subTitle: String,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean,
    stockFilterEnabled: {
      type: Boolean,
      default: true
    },
    stockId: Number
  },
  data() {
    return {
      stockTransactionData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultStockTransactionFilter: {
        startDate: moment().local().subtract(12, 'months').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString(),
        stockId: this.stockId,
        stockInfo: {},
        direction: [StockTransactionDirectionTypes.in, StockTransactionDirectionTypes.out],
        type: [StockTransactionTypes.processed, StockTransactionTypes.purchaseOrder, StockTransactionTypes.salesOrder, StockTransactionTypes.stockTakeAdjust]
      },
      editingStockTransactionFilter: {},
      appliedStockTransactionFilter: {},
    }
  },
  computed: {
    hasStockFilter() {
      return !!this.defaultStockTransactionFilter.stockId && this.stockFilterEnabled
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
    this.StockTransactionTypeLookup = StockTransactionTypeLookup
    this.StockTransactionDirectionTypeLookup = StockTransactionDirectionTypeLookup
  },
  mounted() {
    this.editingStockTransactionFilter = _.clone(this.defaultStockTransactionFilter)
    this.appliedStockTransactionFilter = _.clone(this.defaultStockTransactionFilter)

    this.requestChartData()
  },
  watch: {
    stockId: function() {
      this.defaultStockTransactionFilter.stockId = this.stockId
      this.editingStockTransactionFilter.stockId = this.stockId
      this.appliedStockTransactionFilter.stockId = this.stockId

      this.requestChartData()
    }    
  },
  methods: {
    applyStockTransactionFilter() {
      this.appliedStockTransactionFilter = _.clone(this.editingStockTransactionFilter)

      this.requestChartData()
    },
    resetStockTransactionFilter() {
      this.editingStockTransactionFilter = _.clone(this.defaultStockTransactionFilter)
      this.appliedStockTransactionFilter = _.clone(this.defaultStockTransactionFilter)

      this.requestChartData()
    },
    requestChartData() {
      var stockTransactionFilter = {
        type: 'daily',
        startDate: this.appliedStockTransactionFilter.startDate,
        endDate: this.appliedStockTransactionFilter.endDate,
        stockId: this.appliedStockTransactionFilter.stockId
      }

      if(this.appliedStockTransactionFilter.direction && this.appliedStockTransactionFilter.direction.length > 0) {
        stockTransactionFilter.transactionDirection = this.appliedStockTransactionFilter.direction
      }
      if(this.appliedStockTransactionFilter.type && this.appliedStockTransactionFilter.type.length > 0) {
        stockTransactionFilter.transactionType = this.appliedStockTransactionFilter.type
      }

      this.$apiService.analyticsService.getInventoryTransactionChartData(stockTransactionFilter)
      .then(response => {
        let chartData = response.data

        var transactionTypeList = Object.keys(StockTransactionTypes)

        this.stockTransactionData.xAxis = {
          type: 'category',
          data: _.uniq(chartData.map(chartEntry => moment(chartEntry.date).format('YYYY-MM-DD')))
        }
        this.stockTransactionData.yAxis = [
          { type: 'value' }
        ]
        this.stockTransactionData.series = []


        Object.keys(StockTransactionDirectionTypes).forEach(direction => {
          transactionTypeList.forEach(type => {
            let dataList = chartData.filter(chartEntry => chartEntry.type == type && chartEntry.direction == direction)
            if(dataList.length == 0)
              return null

            this.stockTransactionData.series.push({
              name: this.formatLookupName(direction, StockTransactionDirectionTypeLookup) + ' - ' + this.formatLookupName(type, StockTransactionTypeLookup),
              type: 'bar',
              stack: direction,
              data: dataList.map(chartEntry => {
                return [moment(chartEntry.date).format('YYYY-MM-DD'), chartEntry.totalUsage]
              }),
            })
          })
        })
      })
    },
  }
}
</script>